// Supermove
import {gql} from '@supermove/graphql';
import {MutationError, useForm, useFormMutation} from '@supermove/hooks';
import {LeadProviderEndpoint} from '@supermove/models';

import TagPill from '@shared/modules/Tag/components/TagPill';

export interface UpdateLeadProviderEndpointForm {
  leadProviderEndpointId: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  globalFields?: {
    projectTypeId?: string;
    jobTypeId?: string;
  };
  customFields?: {
    url: string;
  };
  projectTagIds: string[];
  jobTagIds: string[];
}

const useUpdateLeadProviderEndpointMutation = ({
  endpoint,
  onSuccess,
  onError,
}: {
  endpoint: LeadProviderEndpoint;
  onSuccess: () => void;
  onError: (error: MutationError[]) => void;
}) => {
  const updateLeadProviderEndpointForm: UpdateLeadProviderEndpointForm = {
    leadProviderEndpointId: endpoint.id,
    name: endpoint.inboundWebhookEndpoint.name,
    description: endpoint.inboundWebhookEndpoint.description,
    status: endpoint.inboundWebhookEndpoint.status,
    ...(endpoint.leadProviderGlobalIntegration?.id
      ? {
          globalFields: {
            projectTypeId: endpoint.headerFields.jobType?.projectType.id,
            jobTypeId: endpoint.headerFields.jobType?.id,
          },
          customFields: undefined,
        }
      : {
          globalFields: undefined,
          customFields: {
            url: endpoint.inboundWebhookEndpoint.inboundWebhookDestinations?.[0]?.url || '',
          },
        }),
    projectTagIds: endpoint.headerFields.projectTags.map((tag) => `${tag.id}`),
    jobTagIds: endpoint.headerFields.jobTags.map((tag) => `${tag.id}`),
  };

  const form = useForm({
    initialValues: {
      updateLeadProviderEndpointForm,
    },
  });

  const {submitting, handleSubmit} = useFormMutation({
    form,
    mutation: useUpdateLeadProviderEndpointMutation.mutation,
    variables: {
      updateLeadProviderEndpointForm: form.values.updateLeadProviderEndpointForm,
    },
    onSuccess,
    onError,
  });

  return {
    form,
    submitting,
    handleSubmit,
  };
};

// --------------------------------------------------
// Data
// --------------------------------------------------
useUpdateLeadProviderEndpointMutation.mutation = gql`
    mutation useUpdateLeadProviderEndpointMutation($updateLeadProviderEndpointForm: UpdateLeadProviderEndpointForm!) {
        response: updateLeadProviderEndpoint(updateLeadProviderEndpointForm: $updateLeadProviderEndpointForm) {
            ${gql.errors}
            leadProviderEndpoint {
                id
            }
        }
    }
`;

useUpdateLeadProviderEndpointMutation.fragment = gql`
  ${TagPill.fragment}
  fragment useUpdateLeadProviderEndpointMutation on LeadProviderEndpoint {
    id
    leadProviderGlobalIntegration {
      id
      requiresProjectType
    }
    inboundWebhookEndpoint {
      id
      name
      description
      status
      inboundWebhookDestinations {
        id
        url
      }
    }
    headerFields {
      jobType {
        id
        name
        projectType {
          id
          name
        }
      }
      projectTags {
        ...TagPill
        id
      }
      jobTags {
        ...TagPill
        id
      }
    }
  }
`;

export default useUpdateLeadProviderEndpointMutation;
